import Link from 'next/link';
import { StampType, useToast, BannerType, useModal } from '@pelando/components';
import { ArrowUpRightFromSquare } from '@pelando/fontawesome/icons';
import { StoreDeal } from '@/domain/entities/Store';
import {
  AlgoliaEvents,
  EventCategory,
  GeneralDealEvents,
} from '@/domain/events/analytics';
import { emitTrackingEvent } from '@/infra/analytics/emitTrackingEvent';
import useDealUrl from '../../../hooks/dealUrl';
import { emitGoToDealDetailsEvent } from '../../../services/events';
import { StorePageComponentNames } from '../../../services/events/componentNames';
import { HEADER_HEIGHT } from '../../../components/Header/styles';
import { getStampByType } from '../../../services/stamp';
import { ClickoutComponentSource, getDealUrl } from '../../../services/urls';
import {
  ModalContainer,
  ModalHeader,
  ModalCouponStamp,
  ModalCouponTitle,
  ModalContent,
  ModalCopyButton,
  ModalLink,
  ModalButton,
  ModalCouponSubtitleExpiredCoupon,
  modalContainerStyle,
} from './style';
import { useTranslation } from '../../../hooks/useTranslation';

export type CouponModalProps = {
  deal: StoreDeal;
  isExpiredCoupon: boolean;
  currentUserId?: string;
};

enum CouponModalComponentName {
  RevealedButton = 'coupon_modal_revealed_button',
  CopyButton = 'coupon_modal_copy_button',
}

const CouponModal = ({
  deal,
  isExpiredCoupon,
  currentUserId,
}: CouponModalProps) => {
  const { t } = useTranslation('coupons');
  const stamps = {
    discountPercentage: !!deal.discountPercentage,
    discountFixed: !!deal.discountFixed,
    freeShipping: deal.freeShipping,
    price: !!deal.price,
  };
  const stamp = getStampByType(stamps) as StampType;

  const { showToast } = useToast();
  const { closeModal } = useModal();

  const { couponCode, store, id } = deal;
  const coupon = couponCode;
  const couponHash = `#coupon=${id}`;

  const componentSource = ClickoutComponentSource.StorePageGetDiscount;

  const { clickoutUrl } = useDealUrl({
    deal,
    storeUrl: store?.url,
    bodyCoupon: coupon,
    couponHash,
    componentSource,
    showCouponOnDealPage: true,
    currentUserId,
  });

  const handleCopy = () => {
    emitTrackingEvent({
      category: EventCategory.GeneralDeal,
      name: GeneralDealEvents.ClickToCopyCouponCode,
      extra: {
        component_name: CouponModalComponentName.CopyButton,
      },
    });
    showToast({
      titleText: t('show-toast-title-text'),
      topPosition: HEADER_HEIGHT,
      text: t('show-toast-text'),
      bannerType: BannerType.SUCCESS,
    });
  };

  const dealUrl = getDealUrl({
    id: deal.id,
    title: deal.title,
  });

  const buttonText = isExpiredCoupon
    ? t('button-text-expired-modal')
    : t('button-text-active-modal');

  const handleModalButtonClick = () => {
    emitTrackingEvent({
      category: EventCategory.Algolia,
      name: AlgoliaEvents.ClickOut,
      extra: {
        deal_id: deal.id,
      },
    });
    window.open(clickoutUrl as unknown as string);
  };

  return (
    <ModalContainer>
      {isExpiredCoupon && (
        <ModalCouponSubtitleExpiredCoupon>
          {t('modal-coupon-subtitle-expired-coupon')}
        </ModalCouponSubtitleExpiredCoupon>
      )}
      <ModalHeader>
        <ModalCouponStamp
          isExpiredCoupon={isExpiredCoupon}
          type={stamp}
          value={
            deal.discountFixed || deal.discountPercentage || deal.price || 0
          }
        />
        <ModalCouponTitle>{deal.title}</ModalCouponTitle>
      </ModalHeader>
      <ModalContent>
        <ModalCopyButton
          coupon={deal.couponCode || ''}
          onClick={handleCopy}
          copyButtonLabel={t('copy-button-label')}
          ariaLabelIcon={t('aria-label-icon')}
        >
          {deal.couponCode}
        </ModalCopyButton>
        <ModalButton
          onClick={handleModalButtonClick}
          inactive={isExpiredCoupon}
          icon={ArrowUpRightFromSquare}
          iconPosition="right"
        >
          {buttonText}
        </ModalButton>
        <Link href={dealUrl} prefetch={false} legacyBehavior>
          <ModalLink
            onClick={() => {
              closeModal();
              emitGoToDealDetailsEvent(
                StorePageComponentNames.CouponModalSeeDealLink,
                deal.id
              );
            }}
          >
            {t('open-collapse-text')}
          </ModalLink>
        </Link>
      </ModalContent>
    </ModalContainer>
  );
};

export function useCouponModal() {
  const { showModal } = useModal();

  return ({ deal, isExpiredCoupon, currentUserId }: CouponModalProps) =>
    showModal(
      <CouponModal
        deal={deal}
        isExpiredCoupon={isExpiredCoupon}
        currentUserId={currentUserId}
      />,
      {
        style: modalContainerStyle,
      }
    );
}
